import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { bizApi, BIZ_TOKEN_KEY, BIZ_USER_KEY, BIZ_TENANT_KEY } from '@/lib/businessApi';

// Impersonation session key names — stored in sessionStorage (not local) so
// closing the tab automatically ends the impersonation without leaving a
// stale super-admin token behind. sessionStorage also isolates per-tab, so
// a super admin can have two tabs open impersonating different users.
const SA_PREV_TOKEN_KEY = 'biz-sa-prev-token';
const SA_PREV_USER_KEY = 'biz-sa-prev-user';
const SA_PREV_TENANT_KEY = 'biz-sa-prev-tenant';
const IMP_META_KEY = 'biz-impersonation-meta';

// Effective Co-Pilot access, mirrored so a reload doesn't flash the nav.
const BIZ_CAPS_KEY = 'becaim_biz_capabilities';

const BusinessAuthCtx = createContext(null);

function readJson(key, store = localStorage) {
  try { return JSON.parse(store.getItem(key) || 'null'); } catch { return null; }
}

export function BusinessAuthProvider({ children }) {
  const [user, setUser] = useState(() => readJson(BIZ_USER_KEY));
  const [tenant, setTenant] = useState(() => readJson(BIZ_TENANT_KEY));
  const [impersonation, setImpersonation] = useState(() => readJson(IMP_META_KEY, sessionStorage));
  const [capabilities, setCapabilities] = useState(() => readJson(BIZ_CAPS_KEY));
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem(BIZ_TOKEN_KEY);
    if (!token) {
      setUser(null); setTenant(null); setImpersonation(null); setCapabilities(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await bizApi.get('/business/auth/me');
      setUser(data.user);
      setTenant(data.tenant);
      localStorage.setItem(BIZ_USER_KEY, JSON.stringify(data.user));
      localStorage.setItem(BIZ_TENANT_KEY, JSON.stringify(data.tenant));
      // `capabilities` may be absent when talking to an older backend — keep
      // it null in that case so hasCapability() falls back to permissive.
      if (data.capabilities) {
        setCapabilities(data.capabilities);
        localStorage.setItem(BIZ_CAPS_KEY, JSON.stringify(data.capabilities));
      } else {
        setCapabilities(null);
        localStorage.removeItem(BIZ_CAPS_KEY);
      }
      // Backend tells us if this session is an impersonation. Persist that
      // in sessionStorage so the banner survives page reloads (but dies
      // when the tab closes).
      if (data.impersonation) {
        setImpersonation(data.impersonation);
        sessionStorage.setItem(IMP_META_KEY, JSON.stringify(data.impersonation));
      } else {
        setImpersonation(null);
        sessionStorage.removeItem(IMP_META_KEY);
      }
    } catch (e) {
      const status = e?.response?.status;
      console.warn('BusinessAuth: /me failed', status || e?.message);
      if (status === 401 || status === 403) {
        // The token is really invalid: sign out.
        setUser(null); setTenant(null); setImpersonation(null); setCapabilities(null);
      } else {
        // Network error / backend restarting: keep the stored session instead of signing the user out.
        setUser((u) => u || readJson(BIZ_USER_KEY));
        setTenant((t) => t || readJson(BIZ_TENANT_KEY));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback((token, u, t) => {
    localStorage.setItem(BIZ_TOKEN_KEY, token);
    localStorage.setItem(BIZ_USER_KEY, JSON.stringify(u));
    if (t) localStorage.setItem(BIZ_TENANT_KEY, JSON.stringify(t));
    setUser(u);
    if (t) setTenant(t);
    // The login response carries no capabilities, so pull them now rather
    // than leaving the shell permissive until the next full page load.
    refresh();
  }, [refresh]);

  /**
   * Start an impersonation session. Saves the current (super-admin) token
   * to sessionStorage so `exitImpersonation` can restore it, then swaps in
   * the target user's token + identity. Called by the Super Admin Tenants
   * page after `POST /business/super-admin/impersonate/{user_id}` succeeds.
   */
  const startImpersonation = useCallback((impToken, impUser, impTenant, impMeta) => {
    // Stash the super admin's current session — only if we're NOT already
    // impersonating (guards against nested impersonation clobbering the
    // real super-admin token with an impersonated one).
    const alreadyImpersonating = !!sessionStorage.getItem(IMP_META_KEY);
    if (!alreadyImpersonating) {
      const prevToken = localStorage.getItem(BIZ_TOKEN_KEY);
      const prevUser = localStorage.getItem(BIZ_USER_KEY);
      const prevTenant = localStorage.getItem(BIZ_TENANT_KEY);
      if (prevToken) sessionStorage.setItem(SA_PREV_TOKEN_KEY, prevToken);
      if (prevUser) sessionStorage.setItem(SA_PREV_USER_KEY, prevUser);
      if (prevTenant) sessionStorage.setItem(SA_PREV_TENANT_KEY, prevTenant);
    }
    localStorage.setItem(BIZ_TOKEN_KEY, impToken);
    localStorage.setItem(BIZ_USER_KEY, JSON.stringify(impUser));
    if (impTenant) localStorage.setItem(BIZ_TENANT_KEY, JSON.stringify(impTenant));
    sessionStorage.setItem(IMP_META_KEY, JSON.stringify(impMeta));
    setUser(impUser);
    if (impTenant) setTenant(impTenant);
    setImpersonation(impMeta);
    // The impersonated user has their own Co-Pilot access — re-resolve it so
    // the shell reflects THEIR permissions, not the super admin's.
    refresh();
  }, [refresh]);

  /**
   * End impersonation and restore the super-admin session. If we can't
   * find the stashed super-admin token (e.g. sessionStorage was cleared),
   * fall back to a full logout so the user has to sign back in.
   */
  const exitImpersonation = useCallback(() => {
    const prevToken = sessionStorage.getItem(SA_PREV_TOKEN_KEY);
    const prevUser = readJson(SA_PREV_USER_KEY, sessionStorage);
    const prevTenant = readJson(SA_PREV_TENANT_KEY, sessionStorage);
    sessionStorage.removeItem(SA_PREV_TOKEN_KEY);
    sessionStorage.removeItem(SA_PREV_USER_KEY);
    sessionStorage.removeItem(SA_PREV_TENANT_KEY);
    sessionStorage.removeItem(IMP_META_KEY);
    setImpersonation(null);
    if (prevToken) {
      localStorage.setItem(BIZ_TOKEN_KEY, prevToken);
      if (prevUser) localStorage.setItem(BIZ_USER_KEY, JSON.stringify(prevUser));
      if (prevTenant) localStorage.setItem(BIZ_TENANT_KEY, JSON.stringify(prevTenant));
      setUser(prevUser);
      setTenant(prevTenant);
      refresh();
      return { restored: true };
    }
    // No stashed session — fall back to logout.
    localStorage.removeItem(BIZ_TOKEN_KEY);
    localStorage.removeItem(BIZ_USER_KEY);
    localStorage.removeItem(BIZ_TENANT_KEY);
    localStorage.removeItem(BIZ_CAPS_KEY);
    setUser(null); setTenant(null); setCapabilities(null);
    return { restored: false };
  }, [refresh]);

  const logout = useCallback(() => {
    localStorage.removeItem(BIZ_TOKEN_KEY);
    localStorage.removeItem(BIZ_USER_KEY);
    localStorage.removeItem(BIZ_TENANT_KEY);
    localStorage.removeItem(BIZ_CAPS_KEY);
    sessionStorage.removeItem(SA_PREV_TOKEN_KEY);
    sessionStorage.removeItem(SA_PREV_USER_KEY);
    sessionStorage.removeItem(SA_PREV_TENANT_KEY);
    sessionStorage.removeItem(IMP_META_KEY);
    setUser(null); setTenant(null); setImpersonation(null); setCapabilities(null);
  }, []);

  /**
   * Can the current user use this Co-Pilot?
   *
   * Permissive when we have no capability data at all — that means either an
   * older backend or a not-yet-refreshed session, and every workspace that
   * pre-dates the capability layer resolves to "open" server-side. Hiding the
   * Co-Pilots in that case would look like a regression to existing users.
   * Once the backend does send capabilities, they are authoritative.
   *
   * This is presentation only. The API enforces access independently, so a
   * permissive answer here can never grant real access.
   */
  const hasCapability = useCallback((key) => {
    if (!capabilities) return true;
    const entry = capabilities[key];
    if (!entry) return true;
    return !!entry.allowed;
  }, [capabilities]);

  /** Why a capability was granted or refused — for "no access" screens. */
  const capabilityReason = useCallback(
    (key) => capabilities?.[key]?.detail || null, [capabilities]);

  /**
   * Should this workspace be shown pricing?
   *
   * Defaults to true: a tenant with no flag recorded, and anyone not signed
   * in, sees pricing exactly as before. Only an explicit `false` from the
   * platform admin hides it — typically for a customer on a negotiated
   * contract, for whom list prices are noise at best and contradictory at
   * worst. This hides the UI; no plan or subscription data changes.
   */
  const pricingVisible = tenant?.features?.pricing?.visible !== false;

  const value = useMemo(
    () => ({ user, tenant, setUser, setTenant, login, logout, loading, refresh, impersonation, startImpersonation, exitImpersonation, capabilities, hasCapability, capabilityReason, pricingVisible }),
    [user, tenant, login, logout, loading, refresh, impersonation, startImpersonation, exitImpersonation, capabilities, hasCapability, capabilityReason, pricingVisible]
  );

  return <BusinessAuthCtx.Provider value={value}>{children}</BusinessAuthCtx.Provider>;
}

export const useBusinessAuth = () => useContext(BusinessAuthCtx);
